import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import AddModalContent from './components/AddModalContent';
import AddModalButtons from './components/AddModalButtons';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider, Validation } from '../../../interfaces/common';
import { ListFilters, Add, AddApi } from '../../../interfaces/invoice.interface';
import { buildHeadCells, getStartEndOfMonth } from '../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING, APP_SUCCESS_MESSAGES, DEFAULTS } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import InvoiceService from '../../../services/invoice.service';
import PropertyService from '../../../services/property.service';
import UserService from '../../../services/user.service';
import CommonService from '../../../services/common.service';
import { SortOrder, Modules, RouteParamsTypes } from '../../../enums';
import CustomerService from '../../../services/customer.service';
import Modal from '../../../components/Modal';
import { validateAddForm } from './validations/index';
import { toast } from 'react-toastify';
import Toast from '../../../components/Toast';
import Button from '../../../components/Button';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';
import useUnsavedChangesWarning from '../../../hooks/useUnsavedChangesWarning';
import ConfirmationFormRouting from '../../../components/ConfirmationFormRouting';

const initialAddFormData: Add = {
  property: '',
  month: DEFAULTS.month(),
  year: DEFAULTS.year(),
};

const initialAddValidation: Validation = { validate: true, fields: [] };

const initialAddDropdownSearchData = {
  property: '',
  month: '',
  year: '',
  reviewedBy: ''
};

const AddList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const defaultFilters: ListFilters = {
    property: '',
    customer: '',
    invoiceStatus: '',
    createdBy: '',
    searchText: '',
    reviewedBy: '',
    isVerified: '',
    managedBy: '',
    ...getStartEndOfMonth(),
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_invoices', defaultFilters);
  const [shouldBlock, setShouldBlock] = useState(false);

  useEffect(() => {
    setShouldBlock(open);
  }, [open]);

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    property: '',
    customer: '',
    invoiceStatus: '',
    createdBy: '',
    isVerified: '',
    managedBy: ''
  });

  const [kpis, setKpis] = useState<any>({});
  const [removalId, setRemovalId] = useState<string>('');
  const [counter, setCounter] = useState<number>(0);
  const [addFormData, setAddFormData] = useState<Add>(initialAddFormData);
  const [addDropdownSearchData, setAddDropdownSearchData] = useState<any>(initialAddDropdownSearchData);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState<any>(null);
  const [addValidation, setAddValidation] = useState<Validation>(initialAddValidation);

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.invoiceNo,
    VALIDATION_FIELD_MAPPING.invoiceDate,
    VALIDATION_FIELD_MAPPING.customer,
    VALIDATION_FIELD_MAPPING.property,
    VALIDATION_FIELD_MAPPING.commission,
    VALIDATION_FIELD_MAPPING.invoiceAmount,
    VALIDATION_FIELD_MAPPING.amountDue,
    VALIDATION_FIELD_MAPPING.dueDate,
    VALIDATION_FIELD_MAPPING.invoiceStatus,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'invoiceList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy
  } = useSorting({ defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.desc, defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.invoiceNo });

  const {
    page, perPage, setPage, setPerPage
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  useUrlParams({
    type: Modules.invoices, uri: Modules.invoices, perPage, page, order, orderBy,
  });

  const { total, data: invoices, loading } = useFetch({
    service: InvoiceService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.invoiceList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.property, filters.customer, filters.invoiceStatus, filters.createdBy, filters.reviewedBy, filters.isVerified, filters.managedBy, filters.startDate, filters.endDate, counter],
    onSuccess: (response: any) => {
      setKpis({
        totalAmountDue: response.total_amount_dues ?? 0,
        totalInvoiceAmount: response.total_invoice_amount ?? 0,
        totalCommission: response.total_commission ?? 0
      });
    },
    onError: () => {
      setKpis({
        totalAmountDue: 0,
        totalInvoiceAmount: 0,
        totalCommission: 0
      });
    }
  });

  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: properties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.property, is_active: '1' },
    dependency: [dropdownSearchData.property],
  });

  const { data: allCustomers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: customers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.customer, is_active: '1' },
    dependency: [dropdownSearchData.customer]
  });

  const { data: allInvoiceStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.invoiceStatus, searchText: '' },
    dependency: []
  });

  const { data: invoiceStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.invoiceStatus, searchText: dropdownSearchData.invoiceStatus },
    dependency: [dropdownSearchData.invoiceStatus]
  });

  const { data: allCreatedBy } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: createdBy } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.createdBy, is_active: '1' },
    dependency: [dropdownSearchData.createdBy]
  });

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, is_active: '1' },
    dependency: [dropdownSearchData.reviewedBy],
  });

  const { data: allVerificationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.verificationStatus, searchText: '' },
    dependency: []
  });

  const { data: verificationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.verificationStatus, searchText: dropdownSearchData.isVerified },
    dependency: [dropdownSearchData.isVerified]
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.managedBy, is_active: '1' },
    dependency: [dropdownSearchData.managedBy]
  });

  const { data: allMonths } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '' },
    dependency: []
  });

  const { data: months } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: addDropdownSearchData.month },
    dependency: [addDropdownSearchData.month]
  });

  const { data: addProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: addDropdownSearchData.property, is_active: '1' },
    dependency: [addDropdownSearchData.property],
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const headerSection = <TableHeaderSection total={total} kpis={kpis} />

  const handleAddNewClick = () => {
    setOpen(true);
  }

  const handleModalClose = () => {
    setAddFormData(initialAddFormData);
    setAddValidation(initialAddValidation);
    setAddDropdownSearchData(initialAddDropdownSearchData);
    setOpen(false);
    setInvoiceError(null)
  }

  const handleAddDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setAddDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setAddDropdownSearchData]);

  const handleAddInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateAddForm({
      ...addFormData,
      [name]: event.target.value.trim(),
    }, name);
    setAddValidation(newValidation);
  }, [addFormData]);

  const handleAddInputChange = useCallback((event: any, name: string) => {
    setAddFormData((prev) => {
      return {
        ...prev,
        [name]: event.target.value
      };
    });
  }, [setAddFormData]);

  const handleDownload = async (id: string) => {
    const response: any = await InvoiceService.preview(id);

    const dataUrl = `data:application/pdf;base64,${response.pdf_base64}`;
    const fetchResponse = await fetch(dataUrl);
    const blob = await fetchResponse.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  const handleRemoveClick = (id: string) => {
    setRemovalId(id)
  }

  const handleSubmit = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();

    const newAddValidation = validateAddForm(addFormData);
    setAddValidation(newAddValidation);
    if (!newAddValidation.validate) return;
    else setAddLoading(true);

    try {
      const payload: AddApi = {
        property_id: addFormData.property,
        month: addFormData.month,
        year: addFormData.year,
        create_new_invoice: !!invoiceError
      };

      let response: any = await InvoiceService.add(payload);

      if (response?.success !== false) {
        const createdId = response?.id;
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.invoiceCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          handleModalClose();
          navigate(`${Modules.invoices}${RouteParamsTypes.edit}/${createdId}`, { replace: true });
        }, 0);
      }

      setAddLoading(false);
    } catch (e: any) {
      setInvoiceError(e?.data?.data ?? null)
      setAddLoading(false);
    }
  }, [addFormData, invoiceError, navigate]);

  const handleRemove = useCallback(async (id: string) => {
    try {
      setRemoveLoading(true);

      await InvoiceService.remove(id);

      setRemoveLoading(false);
      setCounter((prev) => prev + 1);
      setRemovalId('');
    } catch (e) {
      setRemoveLoading(false);
    }
  }, []);

  const modalAction = (
    <div className='d-flex gap-3'>
      <Button
        className="btn btn-outline-secondary"
        text={t("invoices.addList.pageTexts.confirmModal.cancelBtn")}
        onClick={e => setRemovalId('')}
      />
      <Button
        className="btn btn-danger"
        text={t("invoices.addList.pageTexts.confirmModal.removeBtn")}
        loading={removeLoading}
        onClick={e => handleRemove(removalId)}
      />
    </div>
  )

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection, row: Row, data: invoices || [], pagination: true, headCells, order, orderBy, page, perPage, total, loading, noDataFoundMessage: t('invoices.addList.pageTexts.table.noDataFoundMessage'), setPage, setPerPage, setOrder, setOrderBy,
      rowProps: { onDownload: handleDownload, onRemove: handleRemoveClick }
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, invoices, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header onAddNew={handleAddNewClick} />

      <Filters
        properties={properties}
        allProperties={allProperties}
        customers={customers}
        allCustomers={allCustomers}
        invoiceStatus={invoiceStatus}
        allInvoiceStatus={allInvoiceStatus}
        createdBy={createdBy}
        allCreatedBy={allCreatedBy}
        reviewedByOptions={reviewedByOptions}
        allReviewedByOptions={allReviewedByOptions}
        verificationStatus={verificationStatus}
        allVerificationStatus={allVerificationStatus}
        managers={managers}
        allManagers={allManagers}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />

      <Modal
        open={open}
        onClose={handleModalClose}
        title={t("invoices.addList.pageTexts.modal.title")}
        titleClassName='text-start'
        actionClassName='justify-content-start'
        content={
          <AddModalContent
            formData={addFormData}
            validation={addValidation}
            allProperties={allProperties}
            properties={addProperties}
            allMonths={allMonths}
            months={months}
            dropdownSearchData={addDropdownSearchData}
            invoiceError={invoiceError}
            onDropdownSearchChange={handleAddDropdownSearchInputChange}
            onInputBlur={handleAddInputBlur}
            onInputChange={handleAddInputChange}
          />
        }
        action={
          <AddModalButtons
            loading={addLoading}
            invoiceError={invoiceError}
            onCancel={handleModalClose}
            onSave={handleSubmit}
          />
        }
        maxWidth="sm"
      />

      <Modal
        open={removalId ? true : false}
        onClose={() => setRemovalId('')}
        title={t("invoices.addList.pageTexts.confirmModal.title")}
        content={t("invoices.addList.pageTexts.confirmModal.content")}
        contentClassName="text-center"
        action={modalAction}
        maxWidth="sm"
      />

      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </>
  );
};

export default AddList;
